// Monthly / Annual billing-period switch — a rounded pill above the
// pricing cards. Annual is monthly × 10 (2 months free), computed
// server-side; this only flips which price the cards display.

import type { BillingPeriod } from "@/hooks/usePlans";
import { useDict } from "@/i18n/LocaleProvider";

export function PricingToggle({
  value,
  onChange,
}: {
  value: BillingPeriod;
  onChange: (period: BillingPeriod) => void;
}) {
  const t = useDict().landing.pricing;
  const seg = (active: boolean) =>
    "px-4 py-[6px] rounded-full font-mono text-[12px] font-semibold transition-colors cursor-pointer " +
    (active
      ? "bg-accent text-white"
      : "bg-transparent text-muted hover:text-content");

  return (
    <div className="flex justify-center mb-6">
      {/* Mobile: column → call-out drops below the toggle. sm+: the
          wrapper shrink-wraps the toggle and the call-out is absolutely
          positioned beside it, so the toggle never shifts. */}
      <div className="relative inline-flex flex-col items-center">
        <div
          className="inline-flex items-center gap-1 rounded-full p-1 border border-stroke"
          style={{ background: "rgb(var(--color-bg2))" }}
          role="group"
          aria-label="Billing period"
        >
          <button
            type="button"
            className={seg(value === "monthly")}
            aria-pressed={value === "monthly"}
            onClick={() => onChange("monthly")}
          >
            {t.monthly}
          </button>
          <button
            type="button"
            className={seg(value === "annual")}
            aria-pressed={value === "annual"}
            onClick={() => onChange("annual")}
          >
            {t.annual}
          </button>
        </div>
        {/* Savings call-out — annual only. Below the toggle on mobile,
            beside it (absolute) on sm+ so the toggle stays centered. */}
        {value === "annual" && (
          <span
            className="mt-3 sm:mt-0 sm:absolute sm:left-full sm:top-1/2 sm:-translate-y-1/2 sm:ml-3 whitespace-nowrap font-mono text-[12px] font-bold text-success px-3 py-[5px] rounded-full"
            style={{ background: "rgb(var(--color-success) / 0.15)" }}
          >
            ✦ {t.save}
          </span>
        )}
      </div>
    </div>
  );
}
